import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Eye, Users, Globe, Smartphone, Monitor, Tablet,
    Clock, BarChart3, Loader2, RefreshCw, TrendingUp,
    QrCode, Mail, Share2, Phone, MessageCircle, UserPlus, Contact,
    ShoppingBag, Link2, Filter
} from 'lucide-react';
import { apiMethods } from '@/services/api';

const FUNNEL_PERIODS = [
    { days: 7, label: '7 days' },
    { days: 30, label: '30 days' },
    { days: 90, label: '90 days' },
    { days: 365, label: '12 months' },
];

const pct = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : null);

const ACTIONS = [
    { key: 'call', label: 'Calls', icon: Phone },
    { key: 'text', label: 'Texts', icon: MessageCircle },
    { key: 'email', label: 'Emails', icon: Mail },
    { key: 'share', label: 'Shares', icon: Share2 },
    { key: 'qr_open', label: 'QR opens', icon: QrCode },
    { key: 'website', label: 'Website clicks', icon: Globe },
    { key: 'social', label: 'Social clicks', icon: Link2 },
];

const FunnelSection = ({ cardUuid }) => {
    const [days, setDays] = useState(30);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['cardFunnel', cardUuid, days],
        queryFn: async () => {
            const endDate = new Date();
            const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);
            const res = await apiMethods.card.getDetailedAnalytics(cardUuid, {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
            });
            return res.data?.analytics?.funnel || null;
        },
        enabled: !!cardUuid,
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
        placeholderData: (prev) => prev,
    });

    const funnel = data;
    const totals = funnel?.eventTotals || {};
    const steps = funnel
        ? [
              { key: 'views', label: 'Viewed your card', icon: Eye, value: funnel.views || 0 },
              { key: 'save', label: 'Saved your contact', icon: UserPlus, value: funnel.save_contact || 0 },
              { key: 'exchange', label: 'Shared their info', icon: Contact, value: funnel.exchange_submit || 0 },
              { key: 'shop', label: 'Visited the shop', icon: ShoppingBag, value: funnel.shop_click || 0 },
          ]
        : [];
    const top = Math.max(1, ...steps.map((st) => st.value));
    const actions = ACTIONS.map((a) => ({ ...a, value: totals[a.key] || 0 }));
    if (totals.link) actions.push({ key: 'link', label: 'Other link clicks', icon: Link2, value: totals.link });
    const social = (funnel?.socialByPlatform || []).filter((x) => x.count > 0);

    return (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Filter className="h-5 w-5 mr-2 text-gray-400" />
                        Funnel
                    </h2>
                    <p className="text-sm text-gray-600">From a view to a real connection</p>
                </div>
                <div className="inline-flex self-start rounded-lg border border-gray-200 bg-white p-1" role="group" aria-label="Funnel period">
                    {FUNNEL_PERIODS.map((p) => (
                        <button
                            key={p.days}
                            type="button"
                            onClick={() => setDays(p.days)}
                            aria-pressed={days === p.days}
                            className={`rounded-md px-2.5 py-1 text-xs font-medium sm:text-sm ${
                                days === p.days ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading && !funnel ? (
                <div className="flex justify-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                </div>
            ) : isError || !funnel ? (
                <p className="text-sm text-gray-500 py-6 text-center">Funnel data isn't available right now.</p>
            ) : (
                <>
                    <ol className="space-y-4">
                        {steps.map((st, i) => {
                            const Icon = st.icon;
                            const prev = i > 0 ? steps[i - 1].value : null;
                            const stepRate = i > 0 ? pct(st.value, prev) : null;
                            const ofViews = i > 0 ? pct(st.value, steps[0].value) : null;
                            const width = Math.min(100, (st.value / top) * 100);
                            return (
                                <li key={st.key}>
                                    <div className="flex items-baseline justify-between gap-3 mb-1.5">
                                        <span className="flex items-center text-sm font-medium text-gray-700">
                                            <Icon className="h-4 w-4 mr-2 text-gray-400" />
                                            {st.label}
                                        </span>
                                        <span className="text-sm text-gray-500 whitespace-nowrap">
                                            <span className="text-base font-semibold text-gray-900">{st.value.toLocaleString()}</span>
                                            {stepRate !== null && (
                                                <span className="ml-2" title={`${st.value} of ${prev} from the previous step`}>
                                                    {stepRate}% of previous
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div
                                        className="h-3 w-full rounded bg-gray-100"
                                        title={`${st.label}: ${st.value.toLocaleString()}${ofViews !== null ? ` (${ofViews}% of views)` : ''}`}
                                    >
                                        <div
                                            className="h-3 rounded bg-primary-600 transition-all duration-500"
                                            style={{ width: st.value > 0 ? `max(${width}%, 4px)` : 0 }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                    {steps[0].value === 0 && (
                        <p className="mt-4 text-sm text-gray-500">No views in this period yet. Share your card to fill the funnel.</p>
                    )}

                    <div className="mt-8 border-t border-gray-200 pt-6">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">What visitors tapped</h3>
                        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {actions.map((a) => {
                                const Icon = a.icon;
                                return (
                                    <div key={a.key} className="rounded-lg border border-gray-200 px-3 py-2.5">
                                        <dt className="flex items-center text-xs font-medium text-gray-500">
                                            <Icon className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                                            {a.label}
                                        </dt>
                                        <dd className="mt-1 text-xl font-semibold text-gray-900">{a.value.toLocaleString()}</dd>
                                    </div>
                                );
                            })}
                        </dl>
                        {social.length > 0 && (
                            <p className="mt-3 text-xs text-gray-500">
                                Social clicks by platform:{' '}
                                {social.map((x, i) => (
                                    <span key={x.platform}>
                                        {i > 0 && ' · '}
                                        <span className="capitalize text-gray-700">{x.platform}</span> {x.count}
                                    </span>
                                ))}
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

const CardAnalytics = () => {
    const [selectedHour, setSelectedHour] = useState(null);

    // First fetch user profile to get cardUuid
    const { data: profileData } = useQuery({
        queryKey: ['userProfile'],
        queryFn: async () => {
            const response = await apiMethods.users.getProfile();
            return response.data;
        }
    });

    const cardUuid = profileData?.user?.cardUuid;

    // Fetch analytics data
    const { data: analyticsData, isLoading, refetch } = useQuery({
        queryKey: ['cardAnalytics', cardUuid],
        queryFn: async () => {
            if (!cardUuid) return null;
            const response = await apiMethods.card.getAnalytics(cardUuid);
            return response.data;
        },
        enabled: !!cardUuid,
        refetchInterval: 30 * 1000 // Refresh every 30 seconds for real-time feel
    });

    const analytics = analyticsData?.analytics;

    const getDeviceIcon = (device) => {
        switch (device.toLowerCase()) {
            case 'mobile':
                return Smartphone;
            case 'tablet':
                return Tablet;
            case 'desktop':
                return Monitor;
            default:
                return Monitor;
        }
    };

    const getReferrerIcon = (source) => {
        switch (source.toLowerCase()) {
            case 'qr code':
                return QrCode;
            case 'email':
                return Mail;
            case 'social media':
                return Share2;
            default:
                return Globe;
        }
    };

    const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue' }) => {
        return (
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-600">{label}</p>
                        <p className="mt-2 text-3xl font-semibold text-gray-900">
                            {value !== undefined ? value.toLocaleString() : '-'}
                        </p>
                        {subValue && (
                            <p className="mt-1 text-sm text-gray-500">{subValue}</p>
                        )}
                    </div>
                    <div className={`bg-${color}-100 p-3 rounded-full`}>
                        <Icon className={`h-6 w-6 text-${color}-600`} />
                    </div>
                </div>
            </div>
        );
    };

    const HourlyChart = ({ data }) => {
        if (!data || data.length === 0) {
            return (
                <div className="h-64 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                        <Clock className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                        <p>No hourly data available yet</p>
                        <p className="text-sm mt-1">Views will appear here as they happen</p>
                    </div>
                </div>
            );
        }

        const maxViews = Math.max(...data.map(d => d.views));
        const chartHeight = 200;

        return (
            <div className="relative h-64">
                <div className="absolute inset-0 flex items-end justify-between px-2">
                    {data.map((point, index) => {
                        const height = maxViews > 0 ? (point.views / maxViews) * chartHeight : 0;
                        return (
                            <div
                                key={index}
                                className="flex flex-col items-center flex-1 mx-0.5"
                                onMouseEnter={() => setSelectedHour(point)}
                                onMouseLeave={() => setSelectedHour(null)}
                            >
                                <div className="relative w-full group">
                                    <div
                                        className={`${
                                            selectedHour?.hour === point.hour
                                                ? 'bg-primary-600'
                                                : 'bg-primary-500'
                                        } rounded-t transition-all duration-150 hover:bg-primary-600`}
                                        style={{ height: `${height}px` }}
                                    />
                                    {selectedHour?.hour === point.hour && (
                                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                                            {point.views} views
                                        </div>
                                    )}
                                </div>
                                <span className="text-xs text-gray-500 mt-2">
                                    {point.hour > 12 ? `${point.hour - 12}pm` : `${point.hour}${point.hour === 12 ? 'pm' : 'am'}`}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (isLoading || !profileData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="animate-spin h-8 w-8 text-primary-600" />
            </div>
        );
    }

    if (!cardUuid) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No business card created yet</p>
                </div>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <p className="text-gray-600">Unable to load analytics data</p>
                </div>
            </div>
        );
    }

    // Calculate device percentages
    const totalDeviceViews = Object.values(analytics.deviceTypes || {}).reduce((sum, count) => sum + count, 0);
    const deviceData = Object.entries(analytics.deviceTypes || {}).map(([type, count]) => ({
        type,
        count,
        percentage: totalDeviceViews > 0 ? ((count / totalDeviceViews) * 100).toFixed(1) : 0
    }));

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Card Analytics</h1>
                            <p className="mt-2 text-gray-600">
                                Track your digital business card performance
                            </p>
                        </div>
                        <button
                            onClick={() => refetch()}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </button>
                    </div>

                    {/* Live indicator */}
                    <div className="mt-4 flex items-center text-sm text-gray-500">
                        <span className="relative flex h-3 w-3 mr-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                        </span>
                        Live - Updates every 30 seconds
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard
                        icon={Eye}
                        label="Total Views"
                        value={analytics.totalViews}
                        color="blue"
                    />
                    <StatCard
                        icon={Users}
                        label="Unique Visitors"
                        value={analytics.uniqueVisitors}
                        color="green"
                    />
                    <StatCard
                        icon={Clock}
                        label="Views Today"
                        value={analytics.viewsToday}
                        color="purple"
                    />
                    <StatCard
                        icon={TrendingUp}
                        label="Views This Week"
                        value={analytics.viewsThisWeek}
                        subValue={`${analytics.viewsThisMonth} this month`}
                        color="pink"
                    />
                </div>

                <FunnelSection cardUuid={cardUuid} />

                {/* Popular Hours Chart */}
                {analytics.popularHours && analytics.popularHours.length > 0 && (
                    <div className="bg-white rounded-lg shadow p-6 mb-8">
                        <div className="mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Views by Hour
                            </h2>
                            <p className="text-sm text-gray-600">
                                Peak activity times for your business card
                            </p>
                        </div>
                        <HourlyChart data={analytics.popularHours} />
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Device Breakdown */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Types</h3>
                        <div className="space-y-4">
                            {deviceData.map((device) => {
                                const Icon = getDeviceIcon(device.type);

                                return (
                                    <div key={device.type} className="flex items-center">
                                        <Icon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium text-gray-700 capitalize">
                                                    {device.type}
                                                </span>
                                                <span className="text-sm text-gray-500">
                                                    {device.percentage}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${device.percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-sm text-gray-500">
                                {totalDeviceViews === 0 ? (
                                    'No views yet. Share your card to start seeing analytics!'
                                ) : (
                                    `Total views across all devices: ${totalDeviceViews}`
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Traffic Sources */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Traffic Sources</h3>
                        <div className="space-y-3">
                            {analytics.referrers?.map((referrer, index) => {
                                const Icon = getReferrerIcon(referrer.source);
                                const percentage = analytics.totalViews > 0
                                    ? ((referrer.count / analytics.totalViews) * 100).toFixed(1)
                                    : 0;

                                return (
                                    <div key={index} className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <Icon className="h-4 w-4 text-gray-400 mr-3" />
                                            <span className="text-sm text-gray-700">
                                                {referrer.source}
                                            </span>
                                        </div>
                                        <div className="flex items-center">
                                            <span className="text-sm text-gray-500 mr-2">
                                                {referrer.count} ({percentage}%)
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {(!analytics.referrers || analytics.referrers.length === 0) && (
                            <p className="text-sm text-gray-500 text-center py-4">
                                No referrer data available yet. Share your card to start tracking sources!
                            </p>
                        )}
                    </div>
                </div>

                {/* Analytics Notice */}
                <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <BarChart3 className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                                Real-Time Analytics
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                                <p>
                                    Your analytics update in real-time as people view your card.
                                    Views are tracked when visitors access your digital business card,
                                    download your vCard, or scan your QR code.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CardAnalytics;